// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import removeProductIdToCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/RemoveProductFromCompare";
import ProductAddToListLink from "@insite/content-library/Components/ProductAddToListLink";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductPartNumbers from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import PreviouslyPurchasedBadge from "@werner/App/Components/CustomBadges/PreviouslyPurchasedBadge";
import { CommonProductCardProps, withProductCardCommonProps } from "@werner/App/Components/Product/common";
import ProductActions, { ProductActionsStyle } from "@werner/App/Components/Product/ProductActions";
import ProductGridContainer, { ProductGridContainerStyle } from "@werner/App/Components/Product/ProductGridContainer";
import { getSettingsCollection } from "@werner/App/Store/Selectors/SettingsSelectors";
import Badge from "@werner/MobiusOverrides/Badge/Badge";
import Close from "@werner/XDL/Icons/Close";
import {
    createLinkIconStyles,
    mergeWithCss,
    previouslyPurchasedBadgeStyles,
    sharedProductDescriptionStyles,
    sharedTextBadgeStyle,
} from "@werner/XDL/Utilities";
import React from "react";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { css } from "styled-components";

export interface ProductGridCardStyles {
    productGridContainer?: ProductGridContainerStyle;
    productImage?: ProductImageStyles;
    productDescription?: ProductDescriptionStyles;
    productActions?: ProductActionsStyle;
    removeProductLink?: LinkPresentationProps;
    textBadge?: InjectableCss;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    previouslyPurchasedBadge?: InjectableCss;
}

const productGridCardStyles: ProductGridCardStyles = {
    productImage: {
        textBadge: {
            ...mergeWithCss(sharedTextBadgeStyle, {
                css: css`
                    width: 86px;
                    max-width: unset;
                    position: absolute;
                    top: -30px;
                    left: auto;
                    right: -30px;
                `,
            }),
        },
    } as any,
    productDescription: {
        ...sharedProductDescriptionStyles,
    },
    productActions: {
        productAddToCartButtonStyles: {
            css: css`
                min-width: 150px;
            `,
        },

        productQuantityOrderedStyles: {
            cssOverrides: {
                inputSelect: css`
                    width: 64px;
                `,
            },
        },
    },
    removeProductLink: {
        ...createLinkIconStyles(Close, {
            css: css`
                position: absolute;
                top: 0;
                right: 0;
            `,
        }),
    },
    textBadge: {
        css: css`
            font-size: 10px;
            letter-spacing: 0.55px;
            line-height: 12px;
            font-weight: 400;
            text-transform: uppercase;
        `,
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            min-height: 18px;
            min-width: 86px;
            width: fit-content;
            line-height: 18px;
            white-space: nowrap;
            overflow: hidden;
            & > div {
                padding: 0;
            }
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: auto;
        `,
    },
    // XNG-Changes
    previouslyPurchasedBadge: {
        ...previouslyPurchasedBadgeStyles,
    },
};

type Props = CommonProductCardProps<ProductGridCardStyles>;

const ProductGridCard = ({
    productContext,
    productSettings,
    showPartNumbers = true,
    showPrice = true,
    showAvailability = true,
    showAddToCart = true,
    showAddToList = true,
    showBrand = false,
    showTextBadges = true,
    showImageBadges = true,
    maximumNumberOfTextBadges,
    maximumNumberOfImageBadges,
    extendedStyles,
    actionsTemplate,
}: Props) => {
    const [styles] = useState(() => mergeWithCss(productGridCardStyles, extendedStyles));

    if (!productContext) {
        return null;
    }

    const {
        showOverlayBadge,
        overlayBadge,
        overlayBadgeWrapperStyles,
        badgeAreaWrapperRef,
        overlayBadgeWrapperClickHandler,
    } = useOverlayBadge({
        productContext,
        showTextBadge: showTextBadges,
        showImageBadge: showImageBadges,
        styles,
    });

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    return (
        <ProductGridContainer extendedStyles={styles.productGridContainer}>
            <ProductGridContainer.Image>
                {!showOverlayBadge && <ProductImage product={productContext} extendedStyles={styles.productImage} />}
                {showOverlayBadge && overlayBadge && (
                    <StyledWrapper {...styles.badgeAreaWrapper} ref={badgeAreaWrapperRef}>
                        <ProductImage product={productContext} extendedStyles={styles.productImage} />
                        <StyledWrapper
                            {...overlayBadgeWrapperStyles}
                            onClick={overlayBadgeWrapperClickHandler}
                            data-test-selector="productCarouselBadge"
                        >
                            <Badge
                                type={overlayBadge.badgeType}
                                value={
                                    overlayBadge.badgeType === "Image"
                                        ? overlayBadge.largeImageBadgePath
                                        : overlayBadge.displayText
                                }
                                badgeTextColor={overlayBadge.textColorHexCode}
                                badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                                imageAltText={overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name}
                                textBadgeCss={styles.textBadge}
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                )}
            </ProductGridContainer.Image>
            <ProductGridContainer.Info>
                <ProductDescription product={productContext} extendedStyles={styles.productDescription} />
                {showPartNumbers && (
                    <ProductPartNumbers
                        productNumber={productContext.product.productNumber}
                        customerProductNumber={productContext.product.customerProductNumber}
                        manufacturerItem={productContext.product.manufacturerItem}
                    />
                )}

                <StyledWrapper>
                    {showTextBadges && !!textBadges?.length && (
                        <ProductTextBadges badgeList={textBadges} maxBadges={maximumNumberOfTextBadges ?? 4} />
                    )}
                    {showImageBadges && !!imageBadges?.length && (
                        <ProductImageBadges badgeList={imageBadges} maxBadges={maximumNumberOfImageBadges ?? 4} />
                    )}
                    {/* XNG-Changes */}
                    <PreviouslyPurchasedBadge
                        product={productContext}
                        textBadgeWrapperCss={styles.previouslyPurchasedBadge}
                    />
                </StyledWrapper>
            </ProductGridContainer.Info>

            <ProductGridContainer.Price>
                {showPrice && productSettings.canSeePrices && !productContext.product.isVariantParent && (
                    <ProductPrice
                        product={productContext}
                        showLabel={false}
                        showSavings={true}
                        showSavingsAmount={productSettings.showSavingsAmount}
                        showSavingsPercent={productSettings.showSavingsPercent}
                    />
                )}
            </ProductGridContainer.Price>

            <ProductGridContainer.Availability>
                {productSettings.showInventoryAvailability && showAvailability && <ProductContextAvailability />}
            </ProductGridContainer.Availability>

            <ProductGridContainer.AddToCartActions>
                {showAddToCart && <ProductActions extendedStyles={styles.productActions} />}
            </ProductGridContainer.AddToCartActions>

            <ProductGridContainer.AddToList>
                {showAddToList && (
                    <ProductAddToListLink data-test-selector={`actionsAddToList${productContext.product.id}`} />
                )}
                {actionsTemplate ? actionsTemplate(productContext.product) : null}
            </ProductGridContainer.AddToList>
        </ProductGridContainer>
    );
};

export default withProductCardCommonProps(ProductGridCard);
